/**
 * Layered image hotspots.
 *
 * A cut-out figure on a coloured panel, with markers on the garment that draw
 * connector lines out to floating info cards. Unlike a tooltip hotspot, every
 * card is visible at once — the line is what ties a claim to the part of the
 * garment it is about, so nothing has to be discovered by hovering.
 *
 * Lines are drawn into a single SVG overlay and recomputed on resize. Below the
 * desktop breakpoint the layout collapses to a plain stack and the overlay is
 * emptied: connectors between stacked blocks would cross the figure and say
 * nothing.
 */
import { frame, resize } from '@theme/motion';

const SVG_NS = 'http://www.w3.org/2000/svg';

class LayeredHotspots extends HTMLElement {
  #stopResize;

  #onClick = (event) => {
    if (!this.#isCompact) return;
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('[data-hotspot-marker]');
    if (!control || !this.contains(control)) return;

    const id = control.dataset.hotspotMarker;
    const active = control.getAttribute('aria-expanded') === 'true';
    this.#setActive(active ? null : id);
  };

  #onKeyDown = (event) => {
    if (!this.#isCompact || event.key !== 'Escape') return;
    this.#setActive(null);
  };

  connectedCallback() {
    this.overlay = this.querySelector('[data-hotspot-lines]');
    this.markers = Array.from(this.querySelectorAll('[data-hotspot-marker]'));
    this.cards = Array.from(this.querySelectorAll('[data-hotspot-card]'));
    if (!this.overlay || this.markers.length === 0) return;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);
    this.#syncMobileMode();
    this.#draw();
    this.#stopResize = resize(this, () => frame.render(() => {
      this.#syncMobileMode();
      this.#draw();
    }));
  }

  disconnectedCallback() {
    this.#stopResize?.();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  get #isStacked() {
    return !matchMedia('(min-width: 1024px)').matches;
  }

  get #isCompact() {
    return matchMedia('(max-width: 749px)').matches;
  }

  #syncMobileMode() {
    const compact = this.#isCompact;
    this.toggleAttribute('data-mobile-enhanced', compact);
    this.closest('.nuvrya-layered')?.toggleAttribute('data-mobile-hotspots', compact);
    if (compact) {
      this.#setActive(this.cards.find((card) => card.hasAttribute('data-active'))?.dataset.hotspotCard ?? this.cards[0]?.dataset.hotspotCard);
      return;
    }

    for (const marker of this.markers) marker.setAttribute('aria-expanded', 'false');
    for (const card of this.cards) {
      card.removeAttribute('data-active');
      card.removeAttribute('aria-hidden');
    }
  }

  #setActive(id) {
    for (const marker of this.markers) {
      marker.setAttribute('aria-expanded', marker.dataset.hotspotMarker === id ? 'true' : 'false');
    }
    for (const card of this.cards) {
      const active = card.dataset.hotspotCard === id;
      card.toggleAttribute('data-active', active);
      card.setAttribute('aria-hidden', active ? 'false' : 'true');
    }
  }

  #draw() {
    this.overlay.replaceChildren();

    const rect = this.getBoundingClientRect();
    this.overlay.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`);

    if (this.#isStacked) return;

    for (const marker of this.markers) {
      const card = this.querySelector(`[data-hotspot-card="${marker.dataset.hotspotMarker}"]`);
      if (!card) continue;

      const from = marker.getBoundingClientRect();
      const to = card.getBoundingClientRect();

      const x1 = from.left + from.width / 2 - rect.left;
      const y1 = from.top + from.height / 2 - rect.top;

      // Meet the card on whichever vertical edge faces the marker, so the line
      // never crosses the card it is pointing at.
      const cardIsLeft = to.left + to.width / 2 < x1 + rect.left;
      const x2 = (cardIsLeft ? to.right : to.left) - rect.left;
      const y2 = to.top + to.height / 2 - rect.top;

      // A single elbow rather than a diagonal: the horizontal run reads as a
      // pointer, a diagonal reads as decoration.
      const elbowX = cardIsLeft ? x2 + (x1 - x2) * 0.35 : x2 - (x2 - x1) * 0.35;

      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', `M ${x2} ${y2} H ${elbowX} L ${x1} ${y1}`);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1');
      path.setAttribute('vector-effect', 'non-scaling-stroke');
      this.overlay.appendChild(path);
    }
  }
}

if (!customElements.get('nuvrya-layered-hotspots')) {
  customElements.define('nuvrya-layered-hotspots', LayeredHotspots);
}

export { LayeredHotspots };
